/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationAdapter,
  GamoraFoundationGlue,
  GamoraFoundationGuard,
  GamoraFoundationNodeArgs,
  GamoraFoundationNodeChildMap,
  GamoraFoundationNodeRoot,
} from '../foundation/index.js';
import { assert } from '../utils/assert.js';
import { debugCreate } from '../utils/debug.js';
import { GamoraErrorLock } from '../errors/lock.error.js';
import { GamoraUUIDGuard } from '../guards/uuid.js';
const debug = debugCreate({ namespace: 'gamora:stores:default' });
/**
 * Args
 * @public
 */
export interface GamoraStoreDefaultNodeArgs<
  TAdapter extends GamoraFoundationAdapter,
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
> extends GamoraFoundationNodeArgs<TChildMap> {
  adapter: TAdapter;
}

/**
 * Args
 * @public
 */
export type GamoraStoreDefaultNodeConfiguration = {
  defaultLockTimeToLive: number;
  defaultLockWaitRetryCount: number;
  defaultLockWaitRetryTime: number;
};

/**
 * GrootOAuthClientNode
 * @public
 */
export abstract class GamoraStoreDefaultNode<
  TAdapter extends GamoraFoundationAdapter,
  TSubject extends GamoraFoundationGuard,
  TRoot extends GamoraFoundationNodeRoot = any,
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap> = any,
> extends GamoraFoundationGlue<TChildMap, TRoot> {
  private _adapter?: TAdapter;

  protected _configuration: GamoraStoreDefaultNodeConfiguration = {
    defaultLockTimeToLive: 10 * 1000,
    defaultLockWaitRetryCount: 10, // Reduced from 20 due to exponential backoff
    defaultLockWaitRetryTime: 100, // Initial backoff time in ms
  };

  /**
   * Constructor
   */
  constructor(args: GamoraStoreDefaultNodeArgs<TAdapter, TChildMap>) {
    super(args);

    // Set adapter
    this._setAdapter(args.adapter);
  }

  /**
   * Get adapter
   */
  get adapter() {
    assert(
      this._adapter instanceof GamoraFoundationAdapter,
      'Invalid adapter argument: not an GamoraFoundationAdapter.',
    );
    return this._adapter;
  }

  /**
   * Set adapter
   */
  private _setAdapter(adapter: TAdapter) {
    assert(
      adapter instanceof GamoraFoundationAdapter,
      'Invalid adapter argument: not an GamoraFoundationAdapter.',
    );
    this._adapter = adapter;
  }

  /**
   * Has
   */
  abstract has(uuid: GamoraUUIDGuard, ...args: any[]): Promise<boolean>;

  /**
   * Get
   */
  abstract get(uuid: GamoraUUIDGuard, ...args: any[]): Promise<TSubject>;

  /**
   * Upsert
   */
  abstract upsert(subject: TSubject, ...args: any[]): Promise<void>;

  /**
   * Remove
   */
  abstract remove(uuid: GamoraUUIDGuard, ...args: any[]): Promise<void>;

  /**
   * Lock resource
   */
  abstract lock(
    uuid: GamoraUUIDGuard,
    ttl?: number,
    ...args: any[]
  ): Promise<boolean>;

  /**
   * Unlock resource
   */
  abstract unlock(uuid: GamoraUUIDGuard, ...args: any[]): Promise<boolean>;

  /**
   * Executes function with locking mechanism
   * Note: the function can only run safely for defaultLockTimeToLive
   * @param uuid The uuid
   * @param func The function to execute inside the lock
   */
  async withLock<TRet>(
    uuid: GamoraUUIDGuard,
    func: () => Promise<TRet>,
  ): Promise<TRet> {
    try {
      // Get lock
      await this.waitLock(uuid);

      // Execute function in lock
      return await func();
    } finally {
      await this.unlock(uuid).catch((e) => {
        debug.trace(e?.message, e?.stack);
      });
    }
  }

  /**
   * Tries to obtain a naive lock
   * @param uuid The lock UUID to obtain
   */
  async waitLock(uuid: GamoraUUIDGuard): Promise<void> {
    assert(
      uuid instanceof GamoraUUIDGuard,
      'Invalid uuid argument: not a GamoraUUIDGuard.',
    );

    // Try to lock object with exponential backoff
    return new Promise((resolve, reject) => {
      let retryCount = 0;
      const lockOrRetry = async () => {
        return this.lock(uuid)
          .then((result) => {
            if (result) {
              resolve();
            } else {
              retryCount++;

              if (retryCount < this._configuration.defaultLockWaitRetryCount) {
                // Exponential backoff: 250ms * 2^retryCount, capped at 5 seconds
                const baseBackoff = Math.min(
                  this._configuration.defaultLockWaitRetryTime *
                    Math.pow(2, retryCount),
                  5000,
                );

                // Add random jitter (±10%) to prevent thundering herd
                const jitter = -0.1 + Math.random() * 0.2; // -0.1 to +0.1
                const backoffTime = baseBackoff * (1 + jitter);

                setTimeout(() => {
                  lockOrRetry();
                }, backoffTime);
              } else {
                reject(new GamoraErrorLock('Maximum lock retries exceeded.'));
              }
            }
          })
          .catch((e) => {
            debug.trace(e?.message, e?.stack);
          });
      };

      // Lock or retry
      lockOrRetry();
    });
  }
}
